/* sd-cart-suggest.js
 *
 * Interaction layer for the "Complete your bed" panel. The panel itself is
 * SERVER-RENDERED by snippets/sd-cart-suggest.liquid inside the cart-drawer
 * section (and main-cart-items on /cart) — this file never builds or decides
 * the suggestions. It only enhances:
 *
 *   1. Add — intercepts the row's real /cart/add form POST and performs it as
 *      a section-rendered fetch (Dawn's own pattern), so the drawer refreshes
 *      in place. The refreshed drawer HTML arrives with the panel already
 *      updated by the server. Without JS the form still works (lands on /cart).
 *   2. Notify me — swaps an unavailable row's body for the email form and
 *      writes the Klaviyo "Notify Request" profile properties (same property
 *      format as every other notify-me handler — do not change it; the
 *      waiting-list segment + manual outreach parse it).
 *   3. Dismiss — the × hides the panel and stores the cart's comforter
 *      fingerprint (stamped server-side in data-sd-suggest-fp). The panel
 *      stays hidden only while that fingerprint matches: a NEW comforter add
 *      changes it, so suggestions return with fresh buying intent. Fail-open:
 *      if storage or the observer breaks, the panel shows.
 *
 * All events are delegated on document, so drawer re-renders (which replace
 * the panel wholesale) need no re-wiring and no load-order care.
 * NAMING: "suggest", never "upsell" — the old sd-cart-upsell.js filename and
 * class names sat in ad-blocker filter lists (EasyList carries generic
 * "upsell" patterns) and got the feature blocked/hidden for real shoppers.
 */

(function () {
  const KLAVIYO_COMPANY_ID = 'VayxQv';
  const PROFILES_ENDPOINT = `https://a.klaviyo.com/client/profiles/?company_id=${KLAVIYO_COMPANY_ID}`;
  const DISMISS_KEY = 'sd-suggest-dismissed-fp';
  const CART_ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add';

  // ---------- dismissal (cosmetic, fail-open) ----------
  function storedFp() {
    try { return sessionStorage.getItem(DISMISS_KEY); } catch (e) { return null; }
  }
  function applyDismissal() {
    const fp = storedFp();
    document.querySelectorAll('[data-sd-cart-suggest]').forEach((p) => {
      p.hidden = !!fp && p.getAttribute('data-sd-suggest-fp') === fp;
    });
  }

  // ---------- add-on Add (section-rendered fetch, Dawn's own pattern) ----------
  async function handleAdd(form) {
    const btn = form.querySelector('button[type=submit]');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    try {
      const fd = new FormData(form);
      fd.append('sections', 'cart-drawer,cart-icon-bubble');
      fd.append('sections_url', window.location.pathname);
      const r = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
        body: fd
      });
      if (!r.ok) throw new Error(`cart/add ${r.status}`);
      const resp = await r.json();

      // Standalone /cart page: Dawn's <cart-items> doesn't re-render line items
      // from external adds — reload shows the new item, totals, and the
      // server-updated panel.
      if (document.querySelector('cart-items')) {
        window.location.reload();
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && resp.sections) {
        drawer.renderContents(resp);
        applyDismissal();
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.warn('[sd-cart-suggest] add error', e);
      if (btn) { btn.disabled = false; btn.textContent = 'Try again'; }
    }
  }

  // ---------- notify me ----------
  function showNotifyForm(trigger) {
    const card = trigger.closest('[data-sd-suggest-card]');
    if (!card) return;
    const body = card.querySelector('.sd-suggest__body');
    const label = trigger.getAttribute('data-sd-suggest-label') || '';
    const variantId = trigger.getAttribute('data-sd-suggest-notify') || '';
    card.__sdPrevBody = body.innerHTML;
    card.dataset.sdNotifyMode = '1';
    body.innerHTML = `
      <p class="sd-suggest__heading">Notify me — ${label}</p>
      <form class="sd-suggest__form" data-sd-suggest-form data-variant-id="${variantId}" data-item="${label.replace(/"/g, '&quot;')}">
        <input type="email" name="email" placeholder="your@email.com" required class="sd-launch-mode__input">
        <button type="submit" class="sd-launch-mode__btn">Notify me</button>
        <button type="button" class="sd-suggest__cancel" data-sd-suggest-cancel>Cancel</button>
      </form>
      <p class="sd-suggest__success" hidden>Thanks! We’ll email you the moment it’s in stock.</p>
      <p class="sd-suggest__error" hidden>Something went wrong. Please try again.</p>
    `;
  }

  function cancelNotifyForm(trigger) {
    const card = trigger.closest('[data-sd-suggest-card]');
    if (!card || !card.__sdPrevBody) return;
    card.querySelector('.sd-suggest__body').innerHTML = card.__sdPrevBody;
    delete card.dataset.sdNotifyMode;
    delete card.__sdPrevBody;
  }

  async function handleNotify(form) {
    const email = form.querySelector('[name=email]').value.trim();
    if (!email) return;
    const variantId = form.dataset.variantId;
    const item = form.dataset.item || '';
    const submitBtn = form.querySelector('button[type=submit]');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }
    const card = form.closest('[data-sd-suggest-card]');
    try {
      const properties = {
        'Notify Request': item || 'Sleeping Dove (unspecified)',
        'Notify Request Date': new Date().toISOString().slice(0, 10)
      };
      if (variantId) properties['Notify Variant ID'] = variantId;

      const r = await fetch(PROFILES_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: { type: 'profile', attributes: { email: email, properties: properties } }
        })
      });
      if (r.ok || r.status === 202) {
        form.hidden = true;
        card.querySelector('.sd-suggest__success').hidden = false;
      } else {
        throw new Error(`Klaviyo ${r.status}`);
      }
    } catch (err) {
      console.warn('[sd-cart-suggest] notify error', err);
      card.querySelector('.sd-suggest__error').hidden = false;
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Notify me'; }
    }
  }

  // ---------- delegated events (survive every drawer re-render) ----------
  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest) return;
    const dismissBtn = e.target.closest('[data-sd-suggest-dismiss]');
    if (dismissBtn) {
      const panel = dismissBtn.closest('[data-sd-cart-suggest]');
      try {
        sessionStorage.setItem(DISMISS_KEY, (panel && panel.getAttribute('data-sd-suggest-fp')) || '1');
      } catch (err) { /* private mode — hide for this render only */ }
      document.querySelectorAll('[data-sd-cart-suggest]').forEach((p) => { p.hidden = true; });
      return;
    }
    const notifyBtn = e.target.closest('[data-sd-suggest-notify]');
    if (notifyBtn) { showNotifyForm(notifyBtn); return; }
    const cancelBtn = e.target.closest('[data-sd-suggest-cancel]');
    if (cancelBtn) { cancelNotifyForm(cancelBtn); }
  });

  document.addEventListener('submit', (e) => {
    if (!e.target || !e.target.closest) return;
    const addForm = e.target.closest('[data-sd-suggest-addform]');
    if (addForm) { e.preventDefault(); handleAdd(addForm); return; }
    const notifyForm = e.target.closest('[data-sd-suggest-form]');
    if (notifyForm) { e.preventDefault(); handleNotify(notifyForm); }
  });

  // Re-apply dismissal after Dawn replaces the drawer contents (e.g. a comforter
  // add from a product form). Cosmetic only and fail-open: if this observer
  // never fires, the worst case is a dismissed panel reappearing.
  function init() {
    applyDismissal();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && 'MutationObserver' in window) {
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        setTimeout(() => { scheduled = false; applyDismissal(); }, 50);
      }).observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
